import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Button, Upload, Space, Spin } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { parserOrderCsv } from "../helpers/orderParser";
import ErrorFallback from "./ErrorFallback";
import { parseLineItemCsv } from "../helpers/lineItemParser";
import { addLineItemsToCreateOrderRequests } from "../helpers/createOrderRequestUpdater";
import { useCreateOrdersMutation } from "../app/services/lily";
import queryString from "query-string";

const CreateOrder = ({ version }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [errorList, setErrorList] = useState([]);

  const createOrderRequests = useRef([]);
  const orderData = useRef([]);

  const [orderFileList, setOrderFileList] = useState([]);

  const { authToken, clientId } = queryString.parse(location.search);

  const [
    createOrders,
    {
      isLoading: isUploading,
      data: createOrderData,
      error: createOrderError,
      isError: isCreateOrderError,
      isSuccess: isCreateOrderSuccess,
    },
  ] = useCreateOrdersMutation();

  useEffect(() => {
    if (isCreateOrderSuccess) {
      orderData.current = createOrderData;
    }
  }, [
    isUploading,
    createOrderData,
    isCreateOrderSuccess,
    createOrderError,
    isCreateOrderError,
  ]);

  // Redirect to Order Creation Success page
  useEffect(() => {
    if (isCreateOrderSuccess) {
      navigate(`/home/order-success${location.search}`, {
        state: {
          uploadOrdersRes: createOrderRequests.current.length,
          from: location,
        },
      });
    }
  }, [createOrderData, location, navigate, isCreateOrderSuccess]);

  const getCustomError = (errorData) => {
    return errorData;
  };

  // Redirect to unauthorized page if user is not authenticated
  useEffect(() => {
    if (!isCreateOrderError) return;

    if (
      createOrderError?.originalStatus === 504 &&
      createOrderError?.status === "PARSING_ERROR"
    ) {
      navigate(`/home/order-success?${location.search}`, {
        state: {
          uploadOrdersRes: createOrderRequests.current.length,
          from: location,
        },
      });
      return;
    }

    if (createOrderError.status === 401) navigate("/unauthorized");
    else if (createOrderError.status === 422) {
      const errorData = createOrderError?.data?.message || [];
      const customError = getCustomError(errorData);

      setErrorList(["Some Error occured", customError]);
    } else if (
      createOrderError.status >= 400 &&
      createOrderError.status <= 599
    ) {
      setErrorList([
        "Some Error occured",
        createOrderError?.data?.data?.message ||
          createOrderError?.data?.message,
      ]);
    }
  }, [isCreateOrderError, createOrderError, navigate, location]);

  const resetFiles = () => {
    setOrderFileList([]);
  };

  const onSubmit = async () => {
    try {
      let { data } = await parserOrderCsv(orderFileList[0], version);
      createOrderRequests.current = data.createOrderRequests;

      let parsedLineItems = await parseLineItemCsv(orderFileList[0]);
      createOrderRequests.current = addLineItemsToCreateOrderRequests(
        parsedLineItems,
        createOrderRequests.current
      );

      resetFiles();

      await createOrders({
        clientId,
        authToken,
        reqBody: createOrderRequests.current,
        version,
      });
    } catch (data) {
      const { errors } = data;
      setErrorList([...errorList, errors]);
    }
  };

  const orderFileOptions = {
    multiple: false,
    accept: "text/csv",
    orderFileList,
    onRemove: (file) => {
      const index = orderFileList.indexOf(file);
      const newFileList = orderFileList.slice();
      newFileList.splice(index, 1);
      setErrorList([]);
      return setOrderFileList(newFileList);
    },
    beforeUpload: (files) => {
      setErrorList([]);
      setOrderFileList([files]);
      return false;
    },
  };

  if (
    errorList !== null &&
    errorList !== undefined &&
    Array.isArray(errorList) &&
    errorList.length > 0
  ) {
    return (
      <ErrorFallback
        errorData={errorList}
        cb={() => setErrorList([])}
        cbBtnName="Upload Again"
        title="Upload Failed"
      />
    );
  }

  function shouldDisableButton() {
    return !(orderFileList.length === 1);
  }

  return (
    <div>
      <Row justify="center" style={{ paddingTop: "30px" }}>
        <Space size="large" direction="vertical" align="center">
          <Upload {...orderFileOptions}>
            <Button style={{ width: 350 }} icon={<UploadOutlined />}>
              Select Order File
            </Button>
          </Upload>
          <Button
            style={{ width: 350, marginBottom: "30px" }}
            type="primary"
            disabled={shouldDisableButton()}
            onClick={onSubmit}
          >
            Submit
          </Button>
          {((isUploading && !isCreateOrderSuccess) ||
            (isUploading && isCreateOrderError)) && (
            <Spin tip="Please wait, Processing..." />
          )}
        </Space>
      </Row>
    </div>
  );
};

export default CreateOrder;
